import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  TallyClearDriftRequestModel,
  TallyConfigHistoryModel,
  TallyConfigModel,
  TallyConnectionStatusModel,
  TallyDashboardPageModel,
  TallyDepartmentGodownModel,
  TallyGstRateLedgerModel,
  TallyHostStatusModel,
  TallyFullSyncKind,
  TallyMasterSyncKind,
  TallyMasterSyncRunModel,
  TallyPushResultModel,
  TallyPusherModel,
  TallyUnitMappingModel,
  TallyVoucherPreviewModel
} from '../Models/TallyModel';

/**
 * Tally integration API — Phase 2.
 * One method per endpoint exposed by PmsTallyFunction.
 */
@Injectable({ providedIn: 'root' })
export class TallyService {
  private readonly apiUrl = environment.Api_Url;

  constructor(private http: HttpClient) {}

  /**
   * The latest master refreshes, newest first: one kind for a refresh button, or
   * every kind (kind omitted) for Tally Configuration.
   */
  getMasterSyncRuns(kind: TallyMasterSyncKind | null, take: number): Observable<TallyMasterSyncRunModel[]> {
    const params: { [key: string]: string } = { take: String(take) };
    if (kind) {
      params['kind'] = kind;
    }
    return this.http.get<TallyMasterSyncRunModel[]>(this.apiUrl + 'tally/getmastersyncruns', { params });
  }

  /**
   * Start a manual Full refresh: re-read every ledger or stock item from Tally, in batches, not just the
   * changed ones. Needs Tally Integration Add. 200 carries a confirmation; 400 carries a reason (for
   * example a refresh of that list is already running).
   */
  requestFullSync(kind: TallyFullSyncKind): Observable<string> {
    return this.http.post<string>(this.apiUrl + 'tally/requestfullsync', { Kind: kind });
  }

  /**
   * Whether Tally is there to receive vouchers and who last had it open, for the status
   * bar on the Sync Dashboard and Tally Configuration. Open to either page's View
   * permission, unlike getConfig / getConnectionStatus.
   */
  getHostStatus(): Observable<TallyHostStatusModel> {
    return this.http.get<TallyHostStatusModel>(this.apiUrl + 'tally/gethoststatus');
  }

  /** Single config row for the tenant; the backend returns defaults when none exists yet. */
  getConfig(): Observable<TallyConfigModel> {
    return this.http.get<TallyConfigModel>(this.apiUrl + 'tally/getconfig');
  }

  /** Newest-first configuration change audit trail. */
  getConfigHistory(): Observable<TallyConfigHistoryModel[]> {
    return this.http.get<TallyConfigHistoryModel[]>(this.apiUrl + 'tally/getconfighistory');
  }

  /**
   * Queue a connectivity test for the on-premise agent to run on its next poll.
   * 200 carries a confirmation message; 400 carries an operator-facing reason
   * (transport is not PollingAgent, config not saved, ...).
   */
  requestConnectionTest(): Observable<string> {
    return this.http.post<string>(this.apiUrl + 'tally/requestconnectiontest', {});
  }

  /** Saved outcome of the most recent connectivity test, plus the companies open in Tally. */
  getConnectionStatus(): Observable<TallyConnectionStatusModel> {
    return this.http.get<TallyConnectionStatusModel>(this.apiUrl + 'tally/getconnectionstatus');
  }

  /** One page of invoices matching the filters; the params carry page, pagesize and status. */
  getDashboard(params: Record<string, string> = {}): Observable<TallyDashboardPageModel> {
    return this.http.get<TallyDashboardPageModel>(this.apiUrl + 'tally/getdashboard', { params });
  }

  /** Who has pushed a voucher — the Pushed By dropdown. */
  getPushers(): Observable<TallyPusherModel[]> {
    return this.http.get<TallyPusherModel[]>(this.apiUrl + 'tally/getpushers');
  }

  /** Voucher XML plus readable debit/credit lines for one invoice. */
  getVoucherPreview(invoiceId: number): Observable<TallyVoucherPreviewModel> {
    return this.http.get<TallyVoucherPreviewModel>(this.apiUrl + 'tally/getvoucherpreview/' + invoiceId);
  }

  /** Insert/update the config and append a history row. */
  /** Per-GST-rate Tally ledger mappings, lowest rate first. */
  getGstRateLedgers(): Observable<TallyGstRateLedgerModel[]> {
    return this.http.get<TallyGstRateLedgerModel[]>(this.apiUrl + 'tally/getgstrateledgers');
  }

  saveGstRateLedger(row: TallyGstRateLedgerModel): Observable<TallyGstRateLedgerModel> {
    return this.http.post<TallyGstRateLedgerModel>(this.apiUrl + 'tally/savegstrateledger', row);
  }

  deleteGstRateLedger(tallyGstRateLedgerId: number): Observable<string> {
    return this.http.post<string>(this.apiUrl + 'tally/deletegstrateledger', { TallyGstRateLedgerId: tallyGstRateLedgerId });
  }

  /** Every department with its Tally godown, by name. */
  getDepartmentGodowns(): Observable<TallyDepartmentGodownModel[]> {
    return this.http.get<TallyDepartmentGodownModel[]>(this.apiUrl + 'tally/getdepartmentgodowns');
  }

  saveConfig(config: TallyConfigModel): Observable<TallyConfigModel> {
    return this.http.post<TallyConfigModel>(this.apiUrl + 'tally/saveconfig', config);
  }

  /** Push one invoice. ManualXml transport returns the XML for the caller to download. */
  push(invoiceId: number): Observable<TallyPushResultModel> {
    return this.http.post<TallyPushResultModel>(this.apiUrl + 'tally/push', { InvoiceId: invoiceId });
  }

  /**
   * Record that a drifted voucher's difference has been settled in Tally and
   * re-snapshot the receipts it covers. Changes nothing in Tally itself — the
   * voucher is already posted there and cannot be amended from here. Returns
   * the confirmation message; a 400 carries the reason it could not be cleared.
   */
  clearDrift(tallyVoucherSyncId: number): Observable<string> {
    const body: TallyClearDriftRequestModel = { TallyVoucherSyncId: tallyVoucherSyncId };
    return this.http.post<string>(this.apiUrl + 'tally/cleardrift', body);
  }

  /** All ERP unit → Tally unit mapping rows. */
  getUnitMappings(): Observable<TallyUnitMappingModel[]> {
    return this.http.get<TallyUnitMappingModel[]>(this.apiUrl + 'tally/getunitmappings');
  }

  /** Unit names Tally holds, from the synced stock item mirror — the only values a mapping may use. */
  getTallyUnits(): Observable<string[]> {
    return this.http.get<string[]>(this.apiUrl + 'tally/gettallyunits');
  }

  /** Insert/update one mapping; a blank TallyUnitName removes it. */
  saveUnitMapping(mapping: TallyUnitMappingModel): Observable<string> {
    return this.http.post<string>(this.apiUrl + 'tally/saveunitmapping', mapping);
  }
}
